/**
 * Checkout order/payment logic: billing/shipping info, amounts, tax, submit, pay.
 */
import { useCallback } from "react";
import { sumBy } from "lodash";
import { toast } from "react-toastify";
import { formatMoney, oauth, validateEmail } from "../utils";
import { isAvalaraShop } from "../helpers/order";
import { getShopOptions } from "../helpers/shop";
import {
  getSkuFromOptions,
  isPODShop,
  shouldCollectShipAddress,
} from "../components/shop/helpers/helpers";
import { POD_DIVISION_ID } from "../pod";
import Tax from "../models/Tax";
import type {
  CheckoutState,
  CheckoutProps,
  CheckoutAddress,
  CheckoutContact,
  AddressEntity,
  ContactEntity,
  CheckoutCart,
} from "../components/shop/Checkout/types";
import type { Dispatch, SetStateAction } from "react";
import type { PublicViewContextValue } from "./useCheckoutState";
import { getMissingMandatoryMetadataIds } from "../components/shop/Checkout/checkoutAdditionalInfo";
import type { Shop } from "../interfaces/Shop";

type CartBreakdown = CheckoutCart["items"][number]["breakdowns"][number] & {
  color_id?: string | null;
  size_id?: string | null;
  color_name?: string | null;
  size_name?: string | null;
};

type CartSku = {
  product_sku_id?: string;
  description?: string;
  sku_description?: string;
  options?: Array<{ option_axis: string; option_name: string }>;
};

/** Cart often stores color/size with null product_sku_id; resolve like the SKU matrix UI. */
export function resolveCartLineProductSkuId(
  item: CheckoutCart["items"][number],
  breakdown: CartBreakdown,
): string | undefined {
  if (breakdown.product_sku_id) {
    return breakdown.product_sku_id;
  }

  const skus = (item.skus as CartSku[] | undefined) || [];
  if (skus.length && (breakdown.color_name || breakdown.size_name)) {
    const skusWithOptions = skus.filter((s) => Array.isArray(s.options));
    if (skusWithOptions.length) {
      const matched = getSkuFromOptions(skusWithOptions, {
        color: breakdown.color_name || undefined,
        size: breakdown.size_name || undefined,
      });
      if (matched[0]) {
        return matched[0];
      }
    }

    // sku.description is "Basil / S"; sku_description is "Basil/S".
    if (breakdown.color_name && breakdown.size_name) {
      const spaced = `${breakdown.color_name} / ${breakdown.size_name}`;
      const compact = `${breakdown.color_name}/${breakdown.size_name}`;
      const byDesc = skus.find(
        (s) =>
          s.product_sku_id &&
          (s.description === spaced ||
            s.description === compact ||
            s.sku_description === spaced ||
            s.sku_description === compact),
      );
      if (byDesc?.product_sku_id) {
        return byDesc.product_sku_id;
      }
    }
  }

  const inventoryItems =
    (item.inventory_items as Array<{
      product_sku_id?: string;
      color_id?: string;
      size_id?: string;
    }>) || [];
  const inventoryMatch = inventoryItems.find(
    (ii) =>
      ii.product_sku_id &&
      (!breakdown.color_id || ii.color_id === breakdown.color_id) &&
      (!breakdown.size_id || ii.size_id === breakdown.size_id),
  );
  return inventoryMatch?.product_sku_id;
}

interface UseCheckoutOrderArgs {
  state: CheckoutState;
  setState: Dispatch<SetStateAction<CheckoutState>>;
  props: CheckoutProps;
  context: PublicViewContextValue | undefined;
  entityFromContext: Record<string, unknown> | undefined;
  entityId: string;
  checkCoupon: () => Promise<unknown>;
}

export function useCheckoutOrder({
  state,
  setState,
  props,
  context,
  entityFromContext,
  entityId,
  checkCoupon,
}: UseCheckoutOrderArgs) {
  const entity = entityFromContext;
  const entityType = context?.publicViewEntity?.entityType as
    | string
    | undefined;
  const entityClientId = context?.publicViewEntity?.entityClientId;
  const entityAggregate = context?.publicViewEntity?.entityAggregate;
  const entityBuyInventory = context?.publicViewEntity?.buyInventory;
  const contact =
    (context?.publicViewEntity as { contact?: ContactEntity })?.contact ??
    props.contact;
  const getAddress = props.getAddress;
  const {
    onCheckout,
    onCreateErrorPopup,
    navigate,
    items_metadata,
    onCreateCardConnectPopup,
  } = props;
  const entityIsFree = context?.publicViewEntity?.entityIsFree;

  const getBillingInfo = useCallback(() => {
    const {
      billing_contact_id,
      billing_contact,
      billing_address_id,
      billing_address,
    } = state;
    return {
      contact:
        billing_contact_id && billing_contact_id !== "ADD"
          ? { contact_id: billing_contact_id }
          : billing_contact,
      address:
        billing_address_id && billing_address_id !== "ADD"
          ? { address_id: billing_address_id }
          : billing_address,
    };
  }, [
    state.billing_contact_id,
    state.billing_contact,
    state.billing_address_id,
    state.billing_address,
  ]);

  const getShippingInfo = useCallback(() => {
    const {
      shipping_contact_id,
      shipping_address_id,
      shipping_contact,
      shipping_address,
      billing_address,
      sameAsBilling,
    } = state;
    return sameAsBilling
      ? false
      : {
          contact:
            shipping_contact_id && shipping_contact_id !== "ADD"
              ? { contact_id: shipping_contact_id }
              : shipping_contact,
          address:
            shipping_address_id && shipping_address_id !== "ADD"
              ? { address_id: shipping_address_id }
              : {
                  ...shipping_address,
                  address_company:
                    shipping_address.address_company ||
                    billing_address.address_company,
                },
        };
  }, [
    state.shipping_contact_id,
    state.shipping_address_id,
    state.shipping_contact,
    state.shipping_address,
    state.billing_address,
    state.sameAsBilling,
  ]);

  const getShippingAddress = useCallback(():
    | CheckoutAddress
    | AddressEntity => {
    if (
      entityClientId &&
      entityFromContext?.shipping_address_id !== "" &&
      Number(entityAggregate) === 1 &&
      (!shouldCollectShipAddress(entityFromContext) ||
        Number(entityFromContext?.calculate_checkout_taxes) === 0 ||
        Number(entityFromContext?.tax_custom_ship_address) === 0)
    ) {
      return getAddress(
        String(entityFromContext?.shipping_address_id ?? ""),
      ) as AddressEntity;
    }
    const shippingInfo = getShippingInfo();
    if (shippingInfo && typeof shippingInfo === "object") {
      if (
        shippingInfo.address &&
        "address_id" in (shippingInfo.address as object)
      ) {
        return getAddress(
          (shippingInfo.address as { address_id: string }).address_id,
        ) as AddressEntity;
      }
      return shippingInfo.address as CheckoutAddress;
    }
    return getBillingInfo().address as CheckoutAddress;
  }, [
    entityFromContext,
    entityClientId,
    entityAggregate,
    getAddress,
    getShippingInfo,
    getBillingInfo,
  ]);

  const isAvalara = useCallback((): boolean => {
    const addr = getShippingAddress();
    return Number(entityAggregate) === 1
      ? isAvalaraShop(entity as Parameters<typeof isAvalaraShop>[0])
      : addr?.tax_id === Tax.avalara_tax_id;
  }, [entityAggregate, entity, getShippingAddress]);

  const calculateCheckoutAmounts = useCallback(() => {
    const {
      shipping_address_id,
      orderTotal,
      applied_discount,
      avalara_summary,
    } = state;
    let shipping_address: AddressEntity | CheckoutAddress | null =
      shipping_address_id !== "" && shipping_address_id !== "ADD"
        ? (getAddress(shipping_address_id) as AddressEntity)
        : null;
    const taxCustomShipAddress =
      shouldCollectShipAddress(entityFromContext) &&
      Number(entityFromContext?.tax_custom_ship_address) === 1 &&
      Number(entityFromContext?.calculate_checkout_taxes) === 1;
    if (taxCustomShipAddress) {
      shipping_address = getShippingAddress() as AddressEntity;
    }
    const canCollectTaxes =
      Number(entityAggregate) !== 1 ||
      (Number(entityAggregate) === 1 &&
        Number(entityFromContext?.calculate_checkout_taxes) === 1);

    let tax: { tax_id: string; label: string; percent: string } | null = null;
    if (!isAvalara() && canCollectTaxes) {
      if (entityFromContext?.allow_credit_card_checkout === 1) {
        if (
          Number(entityAggregate) === 0 &&
          shipping_address &&
          (shipping_address as AddressEntity).tax_id &&
          parseFloat((shipping_address as AddressEntity).percent ?? "0") !== 0
        ) {
          tax = {
            tax_id: (shipping_address as AddressEntity).tax_id ?? "",
            label: (shipping_address as AddressEntity).label ?? "",
            percent: (shipping_address as AddressEntity).percent ?? "",
          };
        }
      }
      if (Number(entityAggregate) === 1) {
        const shopOptions = getShopOptions(
          entityFromContext?.shop_options as Parameters<
            typeof getShopOptions
          >[0],
        );
        if (
          taxCustomShipAddress &&
          !state.errors.checkout_tax &&
          shopOptions?.use_zip2tax_default &&
          (shipping_address as AddressEntity)?.tax_id
        ) {
          const sa = shipping_address as AddressEntity;
          tax = {
            tax_id: String(sa.tax_id ?? ""),
            label: String(sa.tax_label ?? ""),
            percent: String(sa.tax_percent ?? ""),
          };
        } else if (
          entityFromContext?.default_tax_id &&
          parseFloat(String(entityFromContext?.default_tax_percent)) !== 0
        ) {
          tax = {
            tax_id: String(entityFromContext.default_tax_id),
            label: String(entityFromContext.default_tax_label ?? ""),
            percent: String(entityFromContext.default_tax_percent ?? ""),
          };
        }
      }
    }

    let modifiedTotal = orderTotal;
    let discountValue = 0;
    if (Number(entityAggregate) === 1 && applied_discount) {
      discountValue = applied_discount.subtracted_value ?? 0;
      modifiedTotal = orderTotal - discountValue;
    }
    modifiedTotal = Math.max(modifiedTotal, 0);
    const ignore_tax_on_shipping =
      (Number(entityAggregate) === 1
        ? entityFromContext?.ignore_tax_on_shipping
        : (shipping_address as AddressEntity)?.ignore_tax_on_shipping) === 1;
    let taxableTotal = ignore_tax_on_shipping
      ? modifiedTotal
      : modifiedTotal + state.shipping_cost;
    taxableTotal = Number(parseFloat(String(taxableTotal)).toFixed(4));
    const taxTotal = tax ? taxableTotal * (parseFloat(tax.percent) / 100) : 0;
    const avalaraTotal =
      sumBy(
        avalara_summary !== false ? avalara_summary : [],
        "taxCalculated",
      ) ?? 0;
    const checkoutTotal = ignore_tax_on_shipping
      ? formatMoney(
          taxableTotal + taxTotal + state.shipping_cost + avalaraTotal,
        )
      : formatMoney(taxableTotal + taxTotal + avalaraTotal);
    return { tax, taxTotal, checkoutTotal, discountValue };
  }, [
    state.shipping_address_id,
    state.orderTotal,
    state.applied_discount,
    state.avalara_summary,
    state.errors.checkout_tax,
    state.shipping_cost,
    entityFromContext,
    entityAggregate,
    getAddress,
    getShippingAddress,
    isAvalara,
  ]);

  const isCheckoutTaxValid = useCallback((): boolean => {
    const { shipping_address_id } = state;
    let shipping_address: AddressEntity | null =
      shipping_address_id !== "" && shipping_address_id !== "ADD"
        ? (getAddress(shipping_address_id) as AddressEntity)
        : null;
    const taxCustomShipAddress =
      shouldCollectShipAddress(entityFromContext) &&
      Number(entityFromContext?.tax_custom_ship_address) === 1 &&
      Number(entityFromContext?.calculate_checkout_taxes) === 1;
    if (taxCustomShipAddress) {
      shipping_address = getShippingAddress() as AddressEntity;
    }
    const canCollectTaxes =
      Number(entityAggregate) !== 1 ||
      (Number(entityAggregate) === 1 &&
        Number(entityFromContext?.calculate_checkout_taxes) === 1);
    if (!isAvalara() && canCollectTaxes) {
      if (Number(entityAggregate) === 1) {
        const shopOptions = getShopOptions(
          entityFromContext?.shop_options as Parameters<
            typeof getShopOptions
          >[0],
        );
        if (
          taxCustomShipAddress &&
          !state.errors.checkout_tax &&
          shopOptions?.use_zip2tax_default
        ) {
          return Boolean((shipping_address as AddressEntity)?.tax_id);
        }
      }
    }
    return true;
  }, [
    state.shipping_address_id,
    state.errors.checkout_tax,
    entityFromContext,
    entityAggregate,
    getAddress,
    getShippingAddress,
    isAvalara,
  ]);

  const checkShopCheckoutLimit = useCallback((): boolean => {
    const perCheckoutLimit = context?.publicViewEntity?.perCheckoutLimit;
    const onClickCart = context?.onClickCart;
    if (
      Number(entityAggregate) === 1 &&
      perCheckoutLimit &&
      props.orderTotal > parseInt(String(perCheckoutLimit), 10)
    ) {
      onClickCart?.();
      return true;
    }
    return false;
  }, [
    context?.publicViewEntity?.perCheckoutLimit,
    context?.onClickCart,
    entityAggregate,
    props.orderTotal,
  ]);

  const isOkToCheckout = useCallback((): boolean => {
    const {
      shipping_contact_id,
      billing_contact_id,
      shipping_address_id,
      billing_address_id,
      shipping_contact,
      billing_contact,
      shipping_address,
      billing_address,
      sameAsBilling,
      cart: cartState,
      customer_po: customerPo,
    } = state;
    const entityForceMinimumQty =
      context?.publicViewEntity?.entityForceMinimumQty;
    const minimumQuantityOk =
      !!cartState.items.length &&
      cartState.items.reduce(
        (
          ok: boolean,
          i: {
            quantity: number;
            inventory_items: unknown[];
            minimum_quantity?: number;
          },
        ) => {
          if (!ok) return ok;
          if (!entityBuyInventory && i.inventory_items.length > 0)
            return i.quantity > 0;
          if (
            (Number(entityAggregate) === 1 &&
              Number(entityForceMinimumQty) === 1) ||
            Number(entityAggregate) === 0
          ) {
            return i.quantity >= (i.minimum_quantity ?? 0);
          }
          return i.quantity > 0;
        },
        true,
      );
    const billingContactOk =
      (billing_contact_id && billing_contact_id !== "ADD") ||
      (billing_contact.contact_name.trim() &&
        validateEmail(billing_contact.contact_email.trim()));
    const shippingContactOk = sameAsBilling
      ? billingContactOk
      : (shipping_contact_id && shipping_contact_id !== "ADD") ||
        (shipping_contact.contact_name.trim() &&
          validateEmail(shipping_contact.contact_email.trim()));
    const billingAddressOk =
      (billing_address_id && billing_address_id !== "ADD") ||
      (!entityClientId
        ? billing_address.address_company.trim() &&
          billing_address.address_line_1.trim()
        : billing_address.address_line_1.trim());
    const shippingAddressOk = sameAsBilling
      ? billingAddressOk
      : (shipping_address_id && shipping_address_id !== "ADD") ||
        (!entityClientId
          ? shipping_address.address_line_1.trim()
          : shipping_address.address_line_1.trim());
    const customerPOOk =
      !!entityBuyInventory ||
      entity?.collect_customer_po !== "REQUIRED" ||
      customerPo.trim().length > 0;

    setState((s) => ({
      ...s,
      errors: {
        billing_contact_id:
          entityFromContext?.billing_contact_type === "LIST" &&
          !billing_contact_id,
        billing_contactcontact_name: !billing_contact.contact_name.trim(),
        billing_contactcontact_email: !validateEmail(
          billing_contact.contact_email.trim(),
        ),
        shipping_contact_id:
          entityFromContext?.shipping_contact_type === "LIST" &&
          !shipping_contact_id,
        shipping_contactcontact_name: !shipping_contact.contact_name.trim(),
        shipping_contactcontact_email: !validateEmail(
          shipping_contact.contact_email.trim(),
        ),
        billing_address_id:
          entityFromContext?.billing_address_type === "LIST" &&
          !billing_address_id,
        billing_addressaddress_company: !billing_address.address_company.trim(),
        billing_addressaddress_line_1: !billing_address.address_line_1.trim(),
        shipping_address_id:
          entityFromContext?.shipping_address_type === "LIST" &&
          !shipping_address_id,
        shipping_addressaddress_line_1: !shipping_address.address_line_1.trim(),
        customer_po: !customerPOOk,
      },
    }));

    return (
      !!minimumQuantityOk &&
      billingContactOk &&
      shippingContactOk &&
      billingAddressOk &&
      shippingAddressOk &&
      customerPOOk
    );
  }, [
    state.shipping_contact_id,
    state.billing_contact_id,
    state.shipping_address_id,
    state.billing_address_id,
    state.shipping_contact,
    state.billing_contact,
    state.shipping_address,
    state.billing_address,
    state.sameAsBilling,
    state.cart,
    state.customer_po,
    entityFromContext,
    entityClientId,
    entityBuyInventory,
    entityAggregate,
    entity?.collect_customer_po,
    context?.publicViewEntity?.entityForceMinimumQty,
    setState,
  ]);

  const checkFreeShopEligibility = useCallback((): Promise<{
    error?: string;
    verifyFreeShop?: string;
  }> => {
    if (entityType === "ORDER") {
      return Promise.resolve({ verifyFreeShop: "false" });
    }
    const data: Record<string, unknown> = {
      shop_id: entityId,
      single_use: entityFromContext?.single_use_for_free_shop,
    };
    if (state.order_contact?.contact_email) {
      data.email = state.order_contact.contact_email.trim();
    }
    return oauth("GET", "shop/verify", data).then(
      ({ json }: { json: { error?: string; verifyFreeShop?: string } }) => {
        if (json.error) onCreateErrorPopup(json.error);
        return json;
      },
    );
  }, [
    entityId,
    entityType,
    entityFromContext?.single_use_for_free_shop,
    state.order_contact?.contact_email,
    onCreateErrorPopup,
  ]);

  const handlePayOrder = useCallback(
    (
      billing_info: unknown,
      shipping_info: unknown,
      breakdowns: unknown[],
      artworks: Record<string, string>,
      comment: string,
      customer: CheckoutContact | null,
    ) => {
      const {
        shipping_range_id,
        order_metadata,
        applied_discount,
        customer_po,
      } = state;
      const { checkoutTotal } = calculateCheckoutAmounts();
      const companyData = entity?.company_data as
        | Record<string, unknown>
        | undefined;

      if (companyData?.forward_ready) {
        onCheckout(
          billing_info,
          shipping_info,
          breakdowns,
          artworks,
          comment,
          null,
          shipping_range_id,
          customer,
          checkoutTotal,
          order_metadata,
          applied_discount,
          customer_po,
          false,
          null,
          navigate,
          items_metadata,
          true,
        )
          .then((json: { forward_checkout_url?: string; error?: string }) => {
            setState((s) => ({ ...s, avalara_summary: false }));

            const forward_checkout_url = json?.forward_checkout_url;
            if (!forward_checkout_url) {
              toast.error(
                json?.error ||
                  "We couldn't start your payment right now. Please try again.",
              );
              return;
            }
            window.location.assign(forward_checkout_url);
          })
          .catch(() => {
            toast.error(
              "We couldn't start your payment right now. Please try again.",
            );
          });
        return;
      }

      if (companyData?.credit_card_software === "STRIPE") {
        if (!companyData?.stripe_public_key) {
          alert(
            "Sorry but we could not proceed currently: credit card software is not properly set up. Please contact your administrator of this shop.",
          );
          return;
        }
        onCheckout(
          billing_info,
          shipping_info,
          breakdowns,
          artworks,
          comment,
          null,
          shipping_range_id,
          customer,
          checkoutTotal,
          order_metadata,
          applied_discount,
          customer_po,
          false,
          null,
          navigate,
          items_metadata,
        )
          .then((json: { order?: { stripe_session_id: string } }) => {
            setState((s) => ({ ...s, avalara_summary: false }));
            const stripe = (
              window as unknown as {
                Stripe: (k: string) => {
                  redirectToCheckout: (o: {
                    sessionId: string;
                  }) => Promise<{ error?: { message: string } }>;
                };
              }
            ).Stripe(String(companyData?.stripe_public_key ?? ""));
            stripe
              .redirectToCheckout({
                sessionId: json?.order?.stripe_session_id ?? "",
              })
              .then((result: { error?: { message: string } }) => {
                if (result.error) {
                  onCreateErrorPopup(
                    "Failed to redirect to Stripe page: " +
                      result.error.message,
                  );
                }
              })
              .catch((error: Error) => {
                onCreateErrorPopup(
                  "Failed to redirect to Stripe page: " + error.message,
                );
              });
          })
          .catch((error: unknown) => {
            if (error) {
              onCreateErrorPopup("Failed to create order: " + String(error));
            }
          });
        return;
      }

      if (
        companyData?.credit_card_software === "CARDCONNECT" &&
        !companyData?.empty_cardconnect_merchid &&
        companyData?.cardconnect_api_ready
      ) {
        onCreateCardConnectPopup(
          entity,
          checkoutTotal,
          billing_info,
          shipping_info,
          breakdowns,
          artworks,
          comment,
          state.shipping_range_id,
          customer,
          state.order_metadata,
          state.applied_discount,
          state.customer_po,
          items_metadata,
        );
        return;
      }

      alert(
        "Sorry but we could not proceed currently: credit card software is not properly set up. Please contact your administrator of this shop.",
      );
    },
    [
      state.shipping_range_id,
      state.order_metadata,
      state.applied_discount,
      state.customer_po,
      onCheckout,
      onCreateErrorPopup,
      onCreateCardConnectPopup,
      navigate,
      items_metadata,
      entity,
      calculateCheckoutAmounts,
      setState,
    ],
  );

  const validatePodOrderBeforeSubmit = useCallback(
    async (customer: CheckoutContact | null): Promise<boolean> => {
      const { cart } = state;
      const shippingAddress = (getShippingAddress() || {}) as CheckoutAddress &
        AddressEntity;

      const items = (cart?.items || [])
        .map((item) => ({
          item_id: item.item_id,
          lines: (item.breakdowns || [])
            .filter((b) => Number(b.quantity) > 0)
            .map((b) => {
              const productSkuId = resolveCartLineProductSkuId(
                item,
                b as CartBreakdown,
              );
              if (!productSkuId) {
                return null;
              }
              return {
                product_sku_id: productSkuId,
                quantity: Number(b.quantity),
              };
            })
            .filter(
              (line): line is { product_sku_id: string; quantity: number } =>
                line !== null,
            ),
        }))
        .filter((item) => item.lines.length > 0);

      if (!items.length) {
        console.error(
          "PoD order validate: no cart lines with resolvable product_sku_id",
          cart?.items,
        );
        setState((s) => ({ ...s, showPodValidationModal: true }));
        return false;
      }

      const customerEmail =
        customer?.contact_email?.trim() ||
        state.order_contact?.contact_email?.trim() ||
        "";

      const customerAddress = {
        name:
          customer?.contact_name ||
          [shippingAddress.address_company, shippingAddress.address_line_1]
            .filter(Boolean)
            .join(" ") ||
          "",
        address_line1: shippingAddress.address_line_1 || "",
        address_line2: shippingAddress.address_line_2 || null,
        city: shippingAddress.address_city || "",
        state: shippingAddress.address_state || "",
        postal_code: shippingAddress.address_postal || "",
        country: shippingAddress.address_country || "",
        phone:
          (shippingAddress as { address_phone?: string }).address_phone || null,
      };

      try {
        // Same as product search: only PoD division today is Fulfill Engine.
        // logout: false — public shop guests have no session; a 401 must not redirect to /logout
        const { json } = await oauth(
          "POST",
          "pod/orders/validate",
          {
            division_id: POD_DIVISION_ID,
            commonsku_shop_id:
              entityId || (entity?.shop_id as string | undefined),
            customer_email: customerEmail || null,
            customer_address: customerAddress,
            items,
          },
          undefined,
          { logout: false },
        );
        if (json?.is_valid) {
          return true;
        }
      } catch (err) {
        console.error("PoD order validate failed", err);
      }

      setState((s) => ({ ...s, showPodValidationModal: true }));
      return false;
    },
    [
      state.cart,
      state.order_contact,
      entity,
      entityId,
      getShippingAddress,
      setState,
    ],
  );

  const handleSubmitOrder = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const {
        comment,
        order_contact,
        shipping_range_id,
        order_metadata,
        cart,
        applied_discount,
        customer_po,
      } = state;

      const { checkoutTotal } = calculateCheckoutAmounts();

      if (Number(entityAggregate) === 0 && !isOkToCheckout()) {
        return;
      }
      if (checkShopCheckoutLimit()) return;

      const billing_info = getBillingInfo();
      const shipping_info = getShippingInfo();
      const breakdowns = cart.items.reduce(
        (
          b: { quantity: number }[],
          i: { breakdowns: { quantity: number }[] },
        ) => b.concat(i.breakdowns.filter((br) => Number(br.quantity) > 0)),
        [],
      );
      const artworks = cart.items.reduce(
        (
          art: Record<string, string>,
          i: { artworks: { artwork_id: string; artwork_file_id: string }[] },
        ) => ({
          ...art,
          ...i.artworks.reduce(
            (
              o: Record<string, string>,
              a: { artwork_id: string; artwork_file_id: string },
            ) => {
              o[a.artwork_id] = a.artwork_file_id;
              return o;
            },
            {},
          ),
        }),
        {},
      );

      const customer =
        order_contact.contact_name !== "" &&
        order_contact.contact_email !== "" &&
        validateEmail(order_contact.contact_email.trim())
          ? order_contact
          : null;

      let metadata_error = false;
      if (Number(entityAggregate) === 1) {
        const missingMetaIds = getMissingMandatoryMetadataIds(
          entityFromContext?.metadata_list as Record<
            string,
            { metadata_id: string; mandatory?: number }
          >,
          order_metadata,
        );
        if (missingMetaIds.length > 0) {
          setState((s) => ({
            ...s,
            errors: {
              ...s.errors,
              ...Object.fromEntries(
                missingMetaIds.map((id) => [`metadata_${id}`, true]),
              ),
            },
          }));
          metadata_error = true;
        }
      }

      if (Number(entityAggregate) === 1 && customer === null) {
        setState((s) => ({
          ...s,
          errors: {
            ...s.errors,
            order_contactcontact_name: order_contact.contact_name === "",
            order_contactcontact_email:
              order_contact.contact_email === "" ||
              !validateEmail(order_contact.contact_email.trim()),
          },
        }));
        return;
      }
      if (metadata_error) return;

      const client_id =
        contact && typeof contact === "object"
          ? ((contact as ContactEntity).company_id ?? null)
          : null;

      const finishSubmit = () => {
        if (Number(entityIsFree) === 1) {
          checkFreeShopEligibility().then(
            (json: { error?: string; verifyFreeShop?: string }) => {
              if (json.error) return;
              if (json.verifyFreeShop === "true") {
                Promise.resolve(
                  onCheckout(
                    billing_info,
                    shipping_info,
                    breakdowns,
                    artworks,
                    comment,
                    false,
                    shipping_range_id,
                    customer,
                    checkoutTotal,
                    order_metadata,
                    null,
                    customer_po,
                    true,
                    client_id ?? undefined,
                    navigate,
                    items_metadata,
                  ),
                ).then(() =>
                  setState((s) => ({ ...s, avalara_summary: false })),
                );
              }
            },
          );
        } else if (applied_discount) {
          checkCoupon().then((json: { error?: string }) => {
            if (json?.error) {
              onCreateErrorPopup(json.error as string);
              setState((s) => ({ ...s, applied_discount: null }));
              return;
            }
            if (
              entityFromContext?.allow_credit_card_checkout === 1 &&
              parseFloat(checkoutTotal) > 0
            ) {
              handlePayOrder(
                billing_info,
                shipping_info,
                breakdowns,
                artworks,
                comment,
                customer,
              );
            } else {
              Promise.resolve(
                onCheckout(
                  billing_info,
                  shipping_info,
                  breakdowns,
                  artworks,
                  comment,
                  false,
                  shipping_range_id,
                  customer,
                  checkoutTotal,
                  order_metadata,
                  applied_discount,
                  customer_po,
                  true,
                  client_id ?? undefined,
                  navigate,
                  items_metadata,
                ),
              ).then(() => setState((s) => ({ ...s, avalara_summary: false })));
            }
          });
        } else if (
          entityFromContext?.allow_credit_card_checkout === 1 &&
          parseFloat(checkoutTotal) > 0 &&
          !entityBuyInventory
        ) {
          handlePayOrder(
            billing_info,
            shipping_info,
            breakdowns,
            artworks,
            comment,
            customer,
          );
        } else {
          Promise.resolve(
            onCheckout(
              billing_info,
              shipping_info,
              breakdowns,
              artworks,
              comment,
              false,
              shipping_range_id,
              customer,
              checkoutTotal,
              order_metadata,
              null,
              customer_po,
              true,
              client_id ?? undefined,
              navigate,
              items_metadata,
            ),
          ).then(() => setState((s) => ({ ...s, avalara_summary: false })));
        }
      };

      if (isPODShop(entity as unknown as Shop)) {
        void validatePodOrderBeforeSubmit(customer).then((ok) => {
          if (ok) {
            finishSubmit();
          }
        });
        return;
      }

      finishSubmit();
    },
    [
      state.comment,
      state.order_contact,
      state.shipping_range_id,
      state.order_metadata,
      state.cart,
      state.applied_discount,
      state.customer_po,
      entity,
      entityIsFree,
      entityFromContext,
      entityAggregate,
      entityBuyInventory,
      contact,
      getBillingInfo,
      getShippingInfo,
      calculateCheckoutAmounts,
      isOkToCheckout,
      checkShopCheckoutLimit,
      checkCoupon,
      handlePayOrder,
      checkFreeShopEligibility,
      validatePodOrderBeforeSubmit,
      onCheckout,
      onCreateErrorPopup,
      navigate,
      items_metadata,
      setState,
    ],
  );

  return {
    getShippingAddress,
    calculateCheckoutAmounts,
    isCheckoutTaxValid,
    isAvalara,
    handleSubmitOrder,
  };
}
